import { createApi } from '@reduxjs/toolkit/query/react';

import { sortByKey } from '@app/shared/utils/common';
import { ApiResponse } from '@app/store/utils/common';
import { updateBalance } from '@app/store/utils/cacheUpdate';
import { BalanceActionType, PriceType } from '@app/shared/types/common';
import { SETTLEMENT_DETAILS_CONFIG } from '@app/shared/constants/settlementDetails';
import { baseQueryWithReauth } from '@app/store/utils/baseQueryWithReauth';
import {
  Settlement,
  PurchaseSettlementPayload,
  PurchaseSettlementResponse,
  BoostSettlementPayload,
  BoostSettlementResponse,
  SettlementTagType,
} from './settlementsTypes';

export const settlementsApi = createApi({
  reducerPath: 'settlementsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(SettlementTagType),
  endpoints: (builder) => ({
    getSettlementById: builder.query<Settlement, string>({
      query: (id) => `/settlements/${id}`,
      providesTags: (_, __, id) => [{ type: SettlementTagType.SETTLEMENTS, id }],
    }),
    getSettlements: builder.query<Settlement[], void>({
      query: () => '/settlements',
      providesTags: [SettlementTagType.SETTLEMENTS],
      transformResponse: (response: ApiResponse<Settlement[]>) => {
        const formatted =
          response.data?.map((item) =>
            item.id <= Object.keys(SETTLEMENT_DETAILS_CONFIG).length
              ? { ...item, hasDetails: true }
              : item
          ) ?? [];

        const sorted = sortByKey(formatted, 'price');

        const lockedFromIndex = Math.max(sorted.length - 3, 0);

        return sorted.map((item, index) =>
          index >= lockedFromIndex ? { ...item, isLocked: true } : item
        );
      },
    }),
    purchaseSettlement: builder.mutation<
      PurchaseSettlementResponse,
      PurchaseSettlementPayload & { settlementPrice: number }
    >({
      query: ({ settlementId }) => ({
        url: '/settlements',
        method: 'POST',
        body: { settlementId },
      }),
      onQueryStarted({ settlementPrice }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          dispatch(settlementsApi.util.invalidateTags([SettlementTagType.SETTLEMENTS]));
          updateBalance(PriceType.TON, BalanceActionType.DECREASE, settlementPrice, dispatch);
        });
      },
    }),
    boostSettlement: builder.mutation<
      BoostSettlementResponse,
      BoostSettlementPayload & { amount: number }
    >({
      query: ({ settlementId, paymentType }) => ({
        url: '/settlements',
        method: 'PATCH',
        body: { settlementId, paymentType },
      }),
      onQueryStarted({ amount, paymentType }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          dispatch(settlementsApi.util.invalidateTags([SettlementTagType.SETTLEMENTS]));
          updateBalance(paymentType, BalanceActionType.DECREASE, amount, dispatch);
        });
      },
    }),
  }),
});

export const {
  useGetSettlementByIdQuery,
  useGetSettlementsQuery,
  usePurchaseSettlementMutation,
  useBoostSettlementMutation,
} = settlementsApi;
